import { AUTH_SESSION_CHANGED_EVENT } from "~/utils/helpers/auth/lifecycle";
import { FetchCredentialsEnum, FetchRedirectEnum, HttpStatusEnum } from "~/utils/enums/common.enums";

export interface IAuthTransportSession {
  sessionEpoch: string;
  getAccessTokenForRequest(origin: string): Promise<string>;
  forceTokenRefresh(): Promise<boolean>;
  handleUnauthorized?(epoch: string): void | Promise<void>;
}

export class AuthSessionChangedError extends Error {
  constructor() {
    super("Authentication session changed during request");
    this.name = "AuthSessionChangedError";
  }
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

interface AuthenticatedFetchOptions {
  eventTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  allowAuthorizationOverride?(input: RequestInfo | URL, init?: RequestInit): boolean;
}

interface AuthenticatedOperationOptions<T> {
  auth: IAuthTransportSession;
  origin: string;
  method?: string;
  signal?: AbortSignal;
  send(token: string, signal: AbortSignal): Promise<T>;
  isUnauthorized?(value: unknown): boolean;
  eventTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
}

interface SessionBoundOperationOptions<T> {
  auth: IAuthTransportSession;
  signal?: AbortSignal;
  send(signal: AbortSignal): Promise<T>;
  eventTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
}

export interface IAuthSessionGuard {
  readonly epoch: string;
  readonly signal: AbortSignal;
  readonly sessionChanged: boolean;
  assertCurrent(): void;
  dispose(): void;
}

export function assertAuthSessionCurrent(auth: Pick<IAuthTransportSession, "sessionEpoch">, epoch: string): void {
  if (auth.sessionEpoch !== epoch) throw new AuthSessionChangedError();
}

export async function consumeSessionBoundStream<T>(
  auth: Pick<IAuthTransportSession, "sessionEpoch">,
  epoch: string,
  reader: Pick<ReadableStreamDefaultReader<T>, "read">,
  onChunk: (chunk: T) => void,
): Promise<void> {
  while (true) {
    const { done, value } = await reader.read();
    assertAuthSessionCurrent(auth, epoch);
    if (done) return;
    onChunk(value);
    assertAuthSessionCurrent(auth, epoch);
  }
}

export function bindReadableStreamToSession<T>(
  auth: IAuthTransportSession,
  epoch: string,
  stream: ReadableStream<T>,
  eventTarget = defaultEventTarget(),
): ReadableStream<T> {
  assertAuthSessionCurrent(auth, epoch);
  const guard = createAuthSessionGuard(auth, undefined, eventTarget);
  if (guard.epoch !== epoch) {
    guard.dispose();
    throw new AuthSessionChangedError();
  }

  const reader = stream.getReader();
  let controller: ReadableStreamDefaultController<T> | undefined;
  let settled = false;

  const dispose = () => {
    if (settled) return;
    settled = true;
    guard.signal.removeEventListener("abort", onAbort);
    guard.dispose();
  };
  const onAbort = () => {
    if (settled) return;
    const error = guard.sessionChanged ? new AuthSessionChangedError() : guard.signal.reason;
    void reader.cancel(error).catch(() => {});
    controller?.error(error);
    dispose();
  };

  return new ReadableStream<T>({
    start(streamController) {
      controller = streamController;
      guard.signal.addEventListener("abort", onAbort, { once: true });
      if (guard.signal.aborted) onAbort();
    },
    async pull(streamController) {
      if (settled) return;
      try {
        const { done, value } = await reader.read();
        guard.assertCurrent();
        if (done) {
          streamController.close();
          dispose();
        } else {
          streamController.enqueue(value);
        }
      } catch (error) {
        streamController.error(guard.sessionChanged ? new AuthSessionChangedError() : error);
        dispose();
      }
    },
    async cancel(reason) {
      dispose();
      await reader.cancel(reason);
    },
  });
}

function defaultEventTarget(): Pick<EventTarget, "addEventListener" | "removeEventListener"> | undefined {
  return typeof window === "undefined" ? undefined : window;
}

export function createAuthSessionGuard(
  auth: IAuthTransportSession,
  externalSignal?: AbortSignal,
  eventTarget = defaultEventTarget(),
): IAuthSessionGuard {
  const epoch = auth.sessionEpoch;
  const controller = new AbortController();
  let changed = false;

  const onSessionChanged = () => {
    changed = true;
    controller.abort(new AuthSessionChangedError());
  };
  const onExternalAbort = () => controller.abort(externalSignal?.reason);

  eventTarget?.addEventListener(AUTH_SESSION_CHANGED_EVENT, onSessionChanged);
  if (externalSignal?.aborted) onExternalAbort();
  else externalSignal?.addEventListener("abort", onExternalAbort, { once: true });

  return {
    epoch,
    signal: controller.signal,
    get sessionChanged() {
      return changed || auth.sessionEpoch !== epoch;
    },
    assertCurrent() {
      if (changed || auth.sessionEpoch !== epoch) throw new AuthSessionChangedError();
    },
    dispose() {
      eventTarget?.removeEventListener(AUTH_SESSION_CHANGED_EVENT, onSessionChanged);
      externalSignal?.removeEventListener("abort", onExternalAbort);
    },
  };
}

function statusOf(value: unknown): number | undefined {
  if (value instanceof Response) return value.status;
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as {
    status?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown };
  };
  const status = candidate.response?.status ?? candidate.status ?? candidate.statusCode;
  return typeof status === "number" ? status : undefined;
}

function isSafeRetryMethod(method?: string): boolean {
  const normalized = (method ?? "GET").toUpperCase();
  return normalized === "GET" || normalized === "HEAD";
}

function originOf(input: RequestInfo | URL): string {
  const value = input instanceof Request ? input.url : input.toString();
  const base = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  return new URL(value, base).origin;
}

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  return init?.method ?? (input instanceof Request ? input.method : "GET");
}

function requestHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  return headers;
}

function bindResponseToSession(response: Response, guard: IAuthSessionGuard): Response {
  // Browsers can expose an empty stream for statuses that forbid a body in a new Response.
  if (!response.body || [204, 205, 304].includes(response.status)) {
    guard.dispose();
    return response;
  }

  const reader = response.body.getReader();
  let streamController: ReadableStreamDefaultController<Uint8Array> | undefined;
  let settled = false;

  const dispose = () => {
    if (settled) return;
    settled = true;
    guard.signal.removeEventListener("abort", onAbort);
    guard.dispose();
  };
  const onAbort = () => {
    if (settled) return;
    const error = guard.sessionChanged ? new AuthSessionChangedError() : guard.signal.reason;
    void reader.cancel(error).catch(() => {});
    streamController?.error(error);
    dispose();
  };

  const body = new ReadableStream<Uint8Array>({
    start(controller) {
      streamController = controller;
      guard.signal.addEventListener("abort", onAbort, { once: true });
      if (guard.signal.aborted) onAbort();
    },
    async pull(controller) {
      if (settled) return;
      try {
        const { done, value } = await reader.read();
        guard.assertCurrent();
        if (done) {
          controller.close();
          dispose();
        } else {
          controller.enqueue(value);
        }
      } catch (error) {
        controller.error(guard.sessionChanged ? new AuthSessionChangedError() : error);
        dispose();
      }
    },
    async cancel(reason) {
      dispose();
      await reader.cancel(reason);
    },
  });

  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

async function isUnauthorizedResponse(response: Response): Promise<boolean> {
  if (response.status !== HttpStatusEnum.UNAUTHORIZED) return false;
  // Orders wraps permission denials in HTTP 401 while preserving 403 in the body.
  // Leave those errors to the resource's caller; they do not invalidate the login.
  const body = await response
    .clone()
    .json()
    .catch(() => null);
  return body?.statusCode !== HttpStatusEnum.FORBIDDEN;
}

export function createAuthenticatedFetch(
  auth: IAuthTransportSession,
  fetchImplementation: FetchLike = globalThis.fetch,
  options: AuthenticatedFetchOptions = {},
): FetchLike {
  return async (input, init) => {
    const guard = createAuthSessionGuard(auth, init?.signal ?? undefined, options.eventTarget ?? defaultEventTarget());
    const method = methodOf(input, init);
    const origin = originOf(input);
    const headers = requestHeaders(input, init);
    const hasAuthorizationOverride =
      headers.has("Authorization") && options.allowAuthorizationOverride?.(input, init) === true;

    const send = async (token?: string) => {
      guard.assertCurrent();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const response = await fetchImplementation(input, {
        ...init,
        method,
        headers,
        credentials: FetchCredentialsEnum.OMIT,
        redirect: FetchRedirectEnum.ERROR,
        signal: guard.signal,
      });
      guard.assertCurrent();
      return response;
    };

    try {
      const token = await auth.getAccessTokenForRequest(origin);
      guard.assertCurrent();
      let response = await send(hasAuthorizationOverride ? undefined : token);

      const unauthorized = await isUnauthorizedResponse(response);
      guard.assertCurrent();
      if (unauthorized) {
        // A business refusal does not establish session loss. Validation owns any required refresh.
        await auth.handleUnauthorized?.(guard.epoch);
        guard.assertCurrent();
        if (!hasAuthorizationOverride && isSafeRetryMethod(method)) {
          const currentToken = await auth.getAccessTokenForRequest(origin);
          guard.assertCurrent();
          if (currentToken !== token) {
            await response.body?.cancel().catch(() => {});
            response = await send(currentToken);
          }
        }
      }

      return bindResponseToSession(response, guard);
    } catch (error) {
      guard.dispose();
      if (guard.sessionChanged) throw new AuthSessionChangedError();
      throw error;
    }
  };
}

export async function runAuthenticatedOperation<T>(options: AuthenticatedOperationOptions<T>): Promise<T> {
  const guard = createAuthSessionGuard(options.auth, options.signal, options.eventTarget ?? defaultEventTarget());
  const isUnauthorized = options.isUnauthorized ?? ((value: unknown) => statusOf(value) === 401);

  let sentToken: string;
  const attempt = async (): Promise<T> => {
    sentToken = await options.auth.getAccessTokenForRequest(options.origin);
    guard.assertCurrent();
    const result = await options.send(sentToken, guard.signal);
    guard.assertCurrent();
    return result;
  };

  const canRetryAfterValidation = async (): Promise<boolean> => {
    await options.auth.handleUnauthorized?.(guard.epoch);
    guard.assertCurrent();
    if (!isSafeRetryMethod(options.method)) return false;
    const currentToken = await options.auth.getAccessTokenForRequest(options.origin);
    guard.assertCurrent();
    return currentToken !== sentToken;
  };

  try {
    let result: T;
    try {
      result = await attempt();
    } catch (error) {
      guard.assertCurrent();
      if (!isUnauthorized(error) || !(await canRetryAfterValidation())) throw error;
      return await attempt();
    }
    if (isUnauthorized(result) && (await canRetryAfterValidation())) return await attempt();
    return result;
  } finally {
    guard.dispose();
  }
}

export async function runSessionBoundOperation<T>(options: SessionBoundOperationOptions<T>): Promise<T> {
  const guard = createAuthSessionGuard(options.auth, options.signal, options.eventTarget ?? defaultEventTarget());
  try {
    const result = await options.send(guard.signal);
    guard.assertCurrent();
    return result;
  } finally {
    guard.dispose();
  }
}

export { AUTH_SESSION_CHANGED_EVENT };
